<mat-card>
  <mat-card-header>
    <mat-card-title>Adjustments &amp; Chargebacks</mat-card-title>
  </mat-card-header>

  <mat-card-content>
    <p class="explanation">
      Use an adjustment when what a charity is owed has to change without a payment: a chargeback on a
      payment behind their contributions, or a correction in their favour. Payouts already made are left
      exactly as they are — the change comes off (or goes on to) what they are owed next.
    </p>

    <mat-form-field class="charity-field">
      <mat-label>Charity</mat-label>
      <!-- data-charities-loaded lets a caller (and the UI tests) tell "no charities yet" from "none at all":
           opening the panel before the list arrives closes it again on the next render. -->
      <mat-select [value]="selectedCharityId ?? allCharitiesOptionId"
                  (valueChange)="onCharitySelected($event)"
                  [attr.data-charities-loaded]="charityOptions.length > 0"
                  data-testid="adjustment-charity-select">
        @for (option of charityOptions; track option.id) {
          <mat-option [value]="option.id">{{ option.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <!-- One charity's position, so it only shows while one charity is selected. -->
    @if (balance) {
      <div class="balance-strip" data-testid="adjustment-balance-strip">
        <span>Earned: <strong>{{ balance.TotalContributions | formatCurrency }}</strong></span>
        <span>Paid out: <strong>{{ balance.TotalPayouts | formatCurrency }}</strong></span>
        <span>Adjustments: <strong data-testid="adjustment-total">{{ balance.TotalAdjustments | formatCurrency }}</strong></span>
        <span>Owed: <strong data-testid="adjustment-amount-owed">{{ balance.AmountOwed | formatCurrency }}</strong></span>
      </div>
    }

    <!-- Recording an adjustment still needs a charity: an adjustment against "all charities" is not a thing.
         The prompt replaces the form rather than showing a form that cannot be submitted. -->
    @if (!selectedCharityId) {
      <p class="empty" data-testid="adjustment-pick-charity-to-record">
        Choose a charity above to record an adjustment against it.
      </p>
    } @else {
      <ng-container *hasPermission="permissionTypes.ModifyCharityAdjustments">
        <form [formGroup]="adjustmentForm" (ngSubmit)="onSubmit()" class="adjustment-form">
          <mat-form-field>
            <mat-label>Direction</mat-label>
            <mat-select formControlName="direction" data-testid="adjustment-direction">
              <mat-option value="reduce" data-testid="adjustment-direction-reduce">Reduce what they are owed</mat-option>
              <mat-option value="increase" data-testid="adjustment-direction-increase">Increase what they are owed</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Amount</mat-label>
            <input matInput
                   type="number"
                   min="0.01"
                   step="0.01"
                   formControlName="amount"
                   data-testid="adjustment-amount-input">
            @if (adjustmentForm.controls.amount.touched && adjustmentForm.controls.amount.invalid) {
              <mat-error>Enter an amount greater than zero.</mat-error>
            }
          </mat-form-field>

          <mat-form-field>
            <mat-label>Effective date</mat-label>
            <input matInput
                   type="date"
                   [max]="today"
                   formControlName="effectiveDate"
                   data-testid="adjustment-date-input">
          </mat-form-field>

          <mat-form-field class="reason-field">
            <mat-label>Reason</mat-label>
            <input matInput
                   maxlength="500"
                   formControlName="reason"
                   data-testid="adjustment-reason-input">
            @if (adjustmentForm.controls.reason.touched && adjustmentForm.controls.reason.invalid) {
              <mat-error>A reason is required.</mat-error>
            }
          </mat-form-field>

          <button mat-flat-button
                  color="primary"
                  type="submit"
                  [disabled]="!canSubmit"
                  data-testid="record-adjustment-button">
            Record adjustment
          </button>
        </form>
      </ng-container>
    }

    <app-table-export-buttons
      testIdPrefix="adjustments"
      [disabled]="uiState === UIStateEnum.ShowLoading"
      (exportCsv)="onExportCsv()"
      (exportPdf)="onExportPdf()">
    </app-table-export-buttons>

    @if (uiState === UIStateEnum.ShowLoading) {
      <app-loading-card-content></app-loading-card-content>
    } @else if (adjustments.length) {
      <table class="adjustments-table" data-testid="adjustments-table">
        <thead>
          <tr>
            @if (!selectedCharityId) {
              <th>Charity</th>
            }
            <th>Effective date</th>
            <th class="num">Amount</th>
            <th>Reason</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @for (adjustment of pagedAdjustments; track adjustment.Id) {
            <tr data-testid="adjustment-row" [class.voided]="adjustment.Voided">
              @if (!selectedCharityId) {
                <td data-testid="adjustment-charity-name">{{ adjustment.CharityName || '—' }}</td>
              }
              <td>{{ formatDate(adjustment.EffectiveDate) }}</td>
              <td class="num">{{ adjustment.Amount | formatCurrency }}</td>
              <td>{{ adjustment.Reason }}</td>
              <!-- A voided adjustment stays listed: one that vanished could not be told apart from one
                   that was never made. -->
              <td>{{ adjustment.Voided ? 'Voided' : 'Applied' }}</td>
              <td>
                @if (!adjustment.Voided) {
                  <ng-container *hasPermission="permissionTypes.ModifyCharityAdjustments">
                    <button mat-stroked-button
                            type="button"
                            (click)="onVoidClick(adjustment.Id)"
                            data-testid="void-adjustment-button">
                      Void
                    </button>
                  </ng-container>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>

      <!-- Paged because the default view is now every adjustment in the game. The exports above still cover the
           whole list, not just this page. -->
      <mat-paginator [length]="adjustments.length"
                     [pageSize]="pageSize"
                     [pageIndex]="pageIndex"
                     [pageSizeOptions]="[25, 50, 100, 250]"
                     (page)="onPageChange($event)"
                     data-testid="adjustments-paginator">
      </mat-paginator>
    } @else {
      <p class="empty" data-testid="adjustments-empty">
        {{ selectedCharityId ? 'No adjustments recorded for this charity.' : 'No adjustments recorded for this game.' }}
      </p>
    }
  </mat-card-content>
</mat-card>
